import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { RequestService } from 'src/app/shared/services/request.service';
import { RequestSambaService } from 'src/app/shared/services/request.samba.service';
import { environment } from 'src/environments/environment';
import { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { UploadSnackComponent } from '../snack-bar-upload/upload-snack.component';
import { catchError, finalize, map } from 'rxjs/operators';
import axios from 'axios';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json;charset=UTF-8',
    'Accept-Language': '1'
  }),
  observe: 'response' as 'body' // to display the full response & as 'body' for type cast
};

@Injectable({ providedIn: 'root' })
export class SambaUploderService {
  isUploading: boolean;
  directoryId: string;
  directoryDRMId: string;

  // State snack & upload
  private progress$ = new BehaviorSubject<number>(0);
  private uploading$ = new BehaviorSubject<boolean>(false);
  private snackRef?: MatSnackBarRef<UploadSnackComponent>;
  private error$ = new BehaviorSubject<boolean>(false);

  private activeUploads = 0;
  private unloadGuardAttached = false;

  //Block refresh guard
  private addUnloadGuard() {
    if (!this.unloadGuardAttached) {
      window.addEventListener('beforeunload', this.blockRefresh);
      this.unloadGuardAttached = true;
    }
    this.activeUploads++;
  }
  private removeUnloadGuard() {
    if (--this.activeUploads === 0 && this.unloadGuardAttached) {
      window.removeEventListener('beforeunload', this.blockRefresh);
      this.unloadGuardAttached = false;
    }
  }
  private readonly blockRefresh = (e: BeforeUnloadEvent) => {
    if (this.activeUploads > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  constructor(
    private requestService: RequestService,
    private requestSambaService: RequestSambaService,
    private snack: MatSnackBar
  ) {
    this.directoryId = environment.directoryId;
    this.directoryDRMId = environment.directoryDRMId;
  }

  get() {
    return this.isUploading;
  }

  set(payload: boolean) {
    this.isUploading = payload;
  }

  startUpload() {
    if (!this.isUploading) {
      this.isUploading = true;
      this.uploading$.next(true);
    }
    this.addUnloadGuard();
  }

  getProgress() {
    return this.progress$.asObservable();
  }
  isError$() {
    return this.error$.asObservable();
  }
  isUploading$() {
    return this.uploading$.asObservable();
  }

  // upload(file: File, drm: boolean, options?: AxiosRequestConfig) {
  //     const formData = new FormData();
  //     formData.append('file', file);

  //     let directory;
  //     if (drm) {
  //         directory = this.directoryDRMId;
  //     } else {
  //         directory = this.directoryId;
  //     }

  //     return this.requestSambaService
  //     .post("directories/" + directory + "/upload", formData, options);
  // }
  upload(file: File, drm: boolean, contentId: number, options: AxiosRequestConfig = {}) {
    const formData = new FormData();
    formData.append('file', file);

    const directory = drm ? this.directoryDRMId : this.directoryId;
    const url = `directories/${directory}/upload`;

    if (!this.snackRef) {
      this.snackRef = this.snack.openFromComponent(UploadSnackComponent, {
        data: { pct: 0, contentId },
        horizontalPosition: 'end',
        verticalPosition: 'bottom',
        duration: undefined,
        panelClass: 'upload-snack'
      });
    }

    const userCb = options.onUploadProgress;
    options.onUploadProgress = (ev: AxiosProgressEvent) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded * 100) / ev.total);
        this.progress$.next(pct);
        this.snackRef?.instance.update(pct);
      }
      userCb?.(ev);
    };

    return this.requestSambaService.post(url, formData, options).pipe(
      map((res) => res.data),
      catchError((err) => {
        this.error$.next(true);
        throw err;
      }),
      finalize(() => this.finish())
    );
  }

  uploadSubtitle(file: File, sambaId: string, language: string, options: AxiosRequestConfig = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);

    const url = `/videos/${sambaId}/subtitles/upload`;
    const userCb = options.onUploadProgress;
    options.onUploadProgress = (ev: AxiosProgressEvent) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded * 100) / ev.total);
        this.progress$.next(pct);
        this.snackRef?.instance.update(pct);
      }
      userCb?.(ev);
    };
    return this.requestSambaService.post(url, formData, options).pipe(
      map((res) => res.data),
      catchError((err) => {
        this.error$.next(true);
        throw err;
      }),
      finalize(() => this.finish())
    );
  }

  updateFile(contentId: any, sambaId: string, duration: any) {
    const formData = new FormData();
    formData.append('contentId', contentId);
    formData.append('sambaId', sambaId);
    formData.append('duration', duration);

    return this.requestService.post('/samba/update', formData);
  }

  private finish() {
    // this.snackRef?.dismiss();
    // this.snackRef = undefined;
    this.progress$.next(0);
    this.uploading$.next(false);
    this.error$.next(false);
    this.isUploading = false;
    this.removeUnloadGuard();
  }

  videoAssets(sambaId: string) {
    return this.requestService.get('/samba/video/assets?sambaId=' + sambaId);
  }

  async getVideoAssets(sambaId: string) {
    const url = `${environment.sambaUrl}videos/${sambaId}/assets`;
    console.log('url getVideoAssets: ', url);
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${environment.sambaToken}`
        }
      });
      console.log('response data: ', response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar assets do vídeo', error);
      throw error;
    }
  }

  async deleteSubtitle(sambaId: string, language: string): Promise<void> {
    const url = `${environment.sambaUrl}videos/${sambaId}/subtitle`;
    try {
      await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${environment.sambaToken}`
        },
        params: {
          language
        }
      });
    } catch (err) {
      console.error('Erro ao deletar legenda: ', err);
      throw err;
    }
  }
}
